import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import {
  APIS,
  USER_DETAILS,
  ROLE_PERMISSIONS,
  EXCLUDED_DOMAINS,
  generateUserRoute,
  generatePlanningRoute,
  LOGIN_ERROR_RESPONSE,
  HTTP_ERRORS,
  ALLOWED_ROLES_WITHOUT_LOCATION,
  ROLES_WITH_LOCATION_REQUIRED,
} from 'app/common/constants';
import { BehaviorSubject, EMPTY, Observable, of } from 'rxjs';
import { HttpService } from './http.service';
import { DEFAULT_PAGE, APP_ROUTES } from 'app/common/constants';
import { ResponseService } from './response.service';
import { SentryErrorHandler } from './sentry-error-handler.service';
import { MetaService } from './meta.service';
import { Role, TenantCentre } from '../../models/meta.model';
import { catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { SKIP_UNAUTHORIZED_HANDLING_HEADER } from '../interceptors/http-context.tokens';
import { POST_LOGIN_REDIRECT_STORAGE_KEY } from '../auth/post-login-redirect';
import { extractLoginErrorEmail } from '../auth/extract-login-error-email.util';

@Injectable({ providedIn: 'root' })
export class AuthUserService {
  private authenticated$: BehaviorSubject<boolean>;
  private userRoles: string[] = [];
  private userLocation: string | null = null;
  private readonly isDomainExcluded: boolean = false;
  private accessPagesSet = new Set<string>();
  private currentLocation$: BehaviorSubject<string | null>;
  private authToken = null;

  constructor(
    private router: Router,
    private responseService: ResponseService,
    private sentryService: SentryErrorHandler,
    private httpService: HttpService,
    private metaService: MetaService
  ) {
    this.userLocation = this.getUserDetails(USER_DETAILS.LOCATION);
    const domain = window.location.hostname;
    this.isDomainExcluded = EXCLUDED_DOMAINS.some((d) =>
      domain?.toLowerCase()?.includes(d.toLowerCase())
    );
    const initialLocation = localStorage.getItem(USER_DETAILS.CURRENT_LOCATION) ?? null;
    this.currentLocation$ = new BehaviorSubject<string | null>(initialLocation);
    this.authToken = localStorage.getItem(USER_DETAILS.TOKEN);
    this.authenticated$ = new BehaviorSubject<boolean>(this.hasToken());
    try {
      const storedRole = this.getUserDetails(USER_DETAILS.ROLES);
      if (storedRole) {
        this.userRoles = JSON.parse(storedRole) ?? null;
        this.setAccessPages(this.userRoles);
      }
    } catch {
      this.userRoles = null;
    }
  }

  private hasToken(): boolean {
    return !!this.authToken;
  }

  get AuthToken(): string {
    return this.authToken;
  }

  get IsDomainExcluded(): boolean {
    return this.isDomainExcluded;
  }

  currentLocation(): Observable<string | null> {
    return this.currentLocation$.asObservable();
  }

  isAuthenticated(): boolean {
    return this.authenticated$.value;
  }

  onAuthChange(): Observable<boolean> {
    return this.authenticated$.asObservable();
  }

  set UserRoles(role: string[]) {
    this.userRoles = role;
  }

  get UserRoles(): string[] {
    return this.userRoles;
  }

  get AccessPages(): Set<string> {
    return this.accessPagesSet;
  }

  hasAccessToPage(accessPage: string): boolean {
    const path = accessPage.split('?')[0];

    if (this.accessPagesSet.has(path)) {
      return true;
    }

    for (const page of this.accessPagesSet) {
      if (page.includes(':')) {
        const regex = new RegExp('^' + page.replace(/:[^/]+/g, '[^/]+') + '$');
        if (regex.test(path)) {
          return true;
        }
      }
    }

    return false;
  }

  setAccessPages(roles: string[]) {
    this.accessPagesSet.clear();
    roles.forEach((role) => {
      const rolePages = ROLE_PERMISSIONS[role] ?? [];
      rolePages.forEach((page) => this.accessPagesSet.add(page));
    });
  }

  get UserLocation(): string {
    return this.userLocation;
  }

  clearAuthData() {
    this.authenticated$.next(false);
    this.currentLocation$.next(null);
    localStorage.clear();
    this.currentLocation$.next(null);
    this.router.navigate([APP_ROUTES.MAIN]).then(() => null);
  }

  /** User logout **/
  logout() {
    this.httpService
      .getData(APIS.AUTH.LOGOUT)
      .pipe(catchError((e) => of(null)))
      .subscribe((r) => {
        this.clearAuthData();
      });
  }

  handleMicrosoftRedirect(): void {
    const urlParams = new URLSearchParams(window.location.hash);
    const idToken = urlParams.get('#id_token');

    if (typeof idToken === 'string' && idToken.length > 0) {
      this.login({
        token: idToken,
        appName: 'frontend',
        tenantId: environment?.tenantId,
      });
    } else {
      this.clearSessionAndNavigate();
    }
  }

  clearSessionAndNavigate() {
    localStorage.clear();
    this.router.navigate([APP_ROUTES.MAIN]).then(() => null);
  }

  /** Set user details **/
  setUserDetails(id: string, value) {
    localStorage.setItem(id, value);
    return;
  }

  /** Get user details **/
  getUserDetails(key: string) {
    return localStorage.getItem(key);
  }

  /**
   * Exchanges httpOnly SSO cookie for a frontend session when another first-party app already signed in.
   * Fails quietly if no cookie or 401 (no error toast / redirect).
   *
   * @see ../../../../backend-v2/docs/README-SSO.md
   */
  trySsoSilentLogin(): void {
    if (this.hasToken()) {
      return;
    }
    const tenantId = environment?.tenantId;
    if (!tenantId) {
      return;
    }
    const headers = new HttpHeaders().set(SKIP_UNAUTHORIZED_HANDLING_HEADER, 'true');
    this.httpService
      .postData(APIS.AUTH.LOGIN, { tenantId, appName: 'frontend' }, false, { headers })
      .pipe(catchError(() => EMPTY))
      .subscribe((res) => {
        if (res && res?.accessToken && res?.user && res?.user?.roles) {
          this.applyLoginResponse(res);
        }
      });
  }

  login(credentials: Record<string, any>) {
    this.httpService
      .postData(APIS.AUTH.LOGIN, credentials)
      .pipe(
        catchError((e) => {
          this.errorHandler(e);
          return EMPTY;
        })
      )
      .subscribe((res) => {
        if (res && res?.accessToken && res?.user && res?.user?.roles) {
          this.applyLoginResponse(res);
        } else {
          this.responseService.setResponse(
            LOGIN_ERROR_RESPONSE.EXCEPTION.MESSAGE,
            LOGIN_ERROR_RESPONSE.EXCEPTION.RESPONSE
          );
          this.router.navigate([APP_ROUTES.AUTH.NOT_APPROVED]);
        }
      });
  }

  private applyLoginResponse(res: Record<string, any>): void {
    this.setUserDetails(USER_DETAILS.TOKEN, res?.accessToken);
    this.authToken = res.accessToken;
    const roles = res?.user?.roles ?? [];
    this.UserRoles = roles;
    this.setUserDetails(USER_DETAILS.ROLES, JSON.stringify(roles));
    this.setUserDetails(USER_DETAILS.NAME, res?.user?.name);
    this.setAccessPages(roles);
    this.setUserDetails(USER_DETAILS.ID, res?.user?.id);
    this.setUserDetails(USER_DETAILS.IS_INTERNAL, res?.user?.isInternal ? 'true' : 'false');

    // Fix: userRoles may be array of IDs (strings), not objects. Check accordingly.
    const userRoles = res?.user?.roles ?? [];
    const hasRoleWithLocationRequired = userRoles
      .map((role) => (typeof role === 'object' ? role.id : role))
      .some((roleId) =>
        ROLES_WITH_LOCATION_REQUIRED.some(
          (requiredId) => (roleId || '').toUpperCase() === (requiredId || '').toUpperCase()
        )
      );

    if (hasRoleWithLocationRequired) {
      console.log('hasRoleWithLocationRequired', hasRoleWithLocationRequired);
      const hasLocation = res?.user?.locations && res?.user?.locations?.length > 0;
      console.log('hasLocation', hasLocation);

      if (hasLocation) {
        const locationId = res?.locationId ?? res?.user?.locations?.[0]?.id;
        console.log('locationId', locationId);
        // Persist full locations array from login response as JSON
        this.setUserDetails(USER_DETAILS.LOCATIONS, JSON.stringify(res?.user?.locations ?? []));
        this.setUserDetails(USER_DETAILS.LOCATION, locationId);
        this.setUserDetails(USER_DETAILS.CURRENT_LOCATION, locationId);
        this.userLocation = locationId;
        this.currentLocation$.next(locationId);
        this.authenticated$.next(true);
        this.navigateToStoredOrDefault();
      } else {
        console.log('Location required but missing: show error page');
        // Location required but missing: show error page
        this.responseService.setResponse(
          LOGIN_ERROR_RESPONSE.LOCATION_REQUIRED.MESSAGE,
          LOGIN_ERROR_RESPONSE.LOCATION_REQUIRED.RESPONSE
        );
        this.router.navigate([APP_ROUTES.AUTH.NOT_APPROVED]);
        return;
      }
    } else {
      console.log('Location optional: use first tenant-centre as default');
      // Location optional: use first tenant-centre as default
      this.metaService
        .getTenantCentres()
        .pipe(
          catchError((err) => {
            // API failed (network, 500, etc.): show error page and stop
            const message =
              err?.error?.message ?? LOGIN_ERROR_RESPONSE.INTERNAL_SERVER_ERROR.MESSAGE;
            this.responseService.setResponse(
              message,
              LOGIN_ERROR_RESPONSE.INTERNAL_SERVER_ERROR.RESPONSE
            );
            this.router.navigate([APP_ROUTES.AUTH.NOT_APPROVED]);
            return EMPTY;
          })
        )
        .subscribe((centres) => {
          console.log('centres', centres);
          if (centres.length > 0) {
            const firstLocationId = centres[0]?.id;
            this.setUserDetails(
              USER_DETAILS.LOCATIONS,
              JSON.stringify(centres.map((centre) => centre.id))
            );
            this.setUserDetails(USER_DETAILS.LOCATION, firstLocationId);
            this.setUserDetails(USER_DETAILS.CURRENT_LOCATION, firstLocationId);
            this.userLocation = firstLocationId;
            this.currentLocation$.next(firstLocationId);
            this.authenticated$.next(true);
            this.navigateToStoredOrDefault();
          } else {
            // API succeeded but no centres: show error page
            console.log('API succeeded but no centres: show error page');
            this.responseService.setResponse(
              LOGIN_ERROR_RESPONSE.INTERNAL_SERVER_ERROR.MESSAGE,
              LOGIN_ERROR_RESPONSE.INTERNAL_SERVER_ERROR.RESPONSE
            );
            this.router.navigate([APP_ROUTES.AUTH.NOT_APPROVED]);
          }
        });
      return;
    }
  }

  isRoleIncluded(roleId: string): boolean {
    return this.userRoles.some((role) => role.toLowerCase() === roleId.toLowerCase());
  }

  isInternalUser(): boolean {
    const isInternal = this.getUserDetails(USER_DETAILS.IS_INTERNAL);
    return isInternal === 'true';
  }

  navigateToDefaultOnRole() {
    if (this.isDomainExcluded) {
      this.router.navigate([DEFAULT_PAGE]).then((r) => null);
      return;
    }

    if (!this.AccessPages.size) {
      this.responseService.setResponse('Not allowed to access the site', 'Access Denied');

      setTimeout(() => {
        this.router.navigate([APP_ROUTES.AUTH.NOT_APPROVED]);
      }, 0);
      return;
    }

    if (this.AccessPages.has(DEFAULT_PAGE)) {
      return this.router.navigate([DEFAULT_PAGE]).then((r) => null);
    }

    if (this.AccessPages.has(generateUserRoute(APP_ROUTES.USER.LIST))) {
      return this.router.navigate([APP_ROUTES.USER.BASE, APP_ROUTES.USER.LIST]).then((r) => null);
    }

    if (this.AccessPages.has(APP_ROUTES.CLIENT.INSPECTIONS)) {
      return this.router.navigate([APP_ROUTES.CLIENT.INSPECTIONS]).then((r) => null);
    }

    if (this.AccessPages.has(generatePlanningRoute(APP_ROUTES.PLANNING.MANAGE_INSPECTIONS))) {
      return this.router
        .navigate([APP_ROUTES.PLANNING.BASE, APP_ROUTES.PLANNING.MANAGE_INSPECTIONS])
        .then((r) => null);
    }
  }

  navigateToStoredOrDefault(): void {
    const storedUrl = sessionStorage.getItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    sessionStorage.removeItem(POST_LOGIN_REDIRECT_STORAGE_KEY);
    if (storedUrl && storedUrl.startsWith('/')) {
      if (this.IsDomainExcluded || this.hasAccessToPage(storedUrl)) {
        console.log('navigating to stored url');
        this.router.navigateByUrl(storedUrl).then(() => null);
        return;
      }
    }
    this.navigateToDefaultOnRole();
  }

  removeItem(key: string) {
    localStorage.removeItem(key);
  }

  setCurrentLocation(locationId: string | null) {
    if (locationId) {
      this.setUserDetails(USER_DETAILS.CURRENT_LOCATION, locationId);
    }
    this.currentLocation$.next(locationId);
  }

  getCurrentLocation(): string | null {
    return this.getUserDetails(USER_DETAILS.CURRENT_LOCATION);
  }

  errorHandler(error: HttpErrorResponse) {
    const attemptedEmail = extractLoginErrorEmail(error?.error);

    if (
      error?.status === 403 &&
      error?.error?.description === HTTP_ERRORS.CUSTOM_ERRORS.FORBIDDEN_CODES.UNDER_REVIEW
    ) {
      this.responseService.setResponse(
        LOGIN_ERROR_RESPONSE.UNDER_REVIEW.MESSAGE,
        LOGIN_ERROR_RESPONSE.UNDER_REVIEW.RESPONSE,
        attemptedEmail
      );
    } else if (
      error?.status === 403 &&
      error?.error?.description === HTTP_ERRORS.CUSTOM_ERRORS.FORBIDDEN_CODES.PERMISSION_DENIED
    ) {
      this.responseService.setResponse(
        LOGIN_ERROR_RESPONSE.PERMISSION_DENIED.MESSAGE,
        LOGIN_ERROR_RESPONSE.PERMISSION_DENIED.RESPONSE,
        attemptedEmail
      );
    } else if (error?.status === 423) {
      if (error?.error?.description === HTTP_ERRORS.CUSTOM_ERRORS.LOCKED_CODES.LOCATION_REQUIRED) {
        this.responseService.setResponse(
          LOGIN_ERROR_RESPONSE.LOCATION_REQUIRED.MESSAGE,
          LOGIN_ERROR_RESPONSE.LOCATION_REQUIRED.RESPONSE,
          attemptedEmail
        );
      } else {
        this.responseService.setResponse(
          LOGIN_ERROR_RESPONSE.SUSPENDED.MESSAGE,
          LOGIN_ERROR_RESPONSE.SUSPENDED.RESPONSE,
          attemptedEmail
        );
      }
    } else {
      this.responseService.setResponse(
        error?.error?.message ?? LOGIN_ERROR_RESPONSE.DEFAULT.MESSAGE,
        LOGIN_ERROR_RESPONSE.DEFAULT.RESPONSE,
        attemptedEmail
      );
    }
    this.sentryService.handleError(error ?? 'Unable to connect to the server!');
    this.router.navigate([APP_ROUTES.AUTH.NOT_APPROVED]);
  }
}
